// Blender-made models (art/blender → public/models/*.glb): loaded once,
// cloned per car, and dressed from the product's livery and driver:
// named materials are recoloured and the Decal_* slots get stickers (the
// product's name, its number, later its logo).
import * as THREE from 'three'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import type { Livery } from './cars/livery'

export type DriverPaint = { visor: string; helmet_primary: string; helmet_secondary: string; helmet_pattern?: string }

// The GLBs are Draco-compressed; the decoder (from three's examples) lives in public/draco.
const loader = new GLTFLoader().setDRACOLoader(new DRACOLoader().setDecoderPath('/draco/'))
// Bump when the GLBs in public/models are regenerated (browser cache).
export const MODELS_VERSION = '2026-10-14.13'
const cache = new Map<string, Promise<THREE.Group>>()

export type ModelName = 'formula' | 'rookie' | 'gt' | 'prototype' | 'hypercar' | 'driver' | 'circuit_gp' | 'circuit_endurance'

export function loadModel(name: ModelName): Promise<THREE.Group> {
  let p = cache.get(name)
  if (!p) {
    p = loader.loadAsync(`/models/${name}.glb?v=${MODELS_VERSION}`).then((gltf) => gltf.scene)
    cache.set(name, p)
  }
  return p
}

const FONT = '"Saira Condensed", "Arial Narrow", sans-serif'

function contrast(hex: string) {
  const n = parseInt(hex.replace('#', ''), 16)
  return 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255) > 150 ? '#141416' : '#F7F7F5'
}

type StickerOpts = { aspect: number; color?: string; panel?: string; italic?: boolean; wide?: boolean }

// A sticker: text centred on a transparent canvas, sized to the slot's aspect;
// with a logo, the logo sits on the left and the text fills the rest.
function paintSticker(canvas: HTMLCanvasElement, text: string, opts: StickerOpts, logo?: HTMLImageElement) {
  const { width: w, height: h } = canvas
  const x = canvas.getContext('2d')!
  x.clearRect(0, 0, w, h)
  if (opts.panel) {
    x.fillStyle = opts.panel
    x.beginPath()
    x.roundRect(4, 4, w - 8, h - 8, 18)
    x.fill()
  }
  if (logo && logo.naturalWidth && opts.wide) {
    // A wordmark: the logo alone, as large as the sticker allows.
    const scale = Math.min((h * 0.86) / logo.naturalHeight, (w * 0.92) / logo.naturalWidth)
    const lw = logo.naturalWidth * scale
    const lh = logo.naturalHeight * scale
    x.drawImage(logo, (w - lw) / 2, (h - lh) / 2, lw, lh)
    return
  }
  let left = 0
  if (logo && logo.naturalWidth) {
    const box = h * 0.78
    const scale = Math.min(box / logo.naturalWidth, box / logo.naturalHeight)
    const lw = logo.naturalWidth * scale
    const lh = logo.naturalHeight * scale
    const lx = h * 0.14 + (box - lw) / 2
    x.drawImage(logo, lx, (h - lh) / 2, lw, lh)
    left = h * 0.14 + box + h * 0.1
  }
  x.fillStyle = opts.color ?? '#F7F7F5'
  x.textAlign = 'center'
  x.textBaseline = 'middle'
  x.font = `${opts.italic === false ? '' : 'italic '}800 ${h * 0.78}px ${FONT}`
  x.fillText(text, left + (w - left) / 2, h * 0.54, (w - left) * 0.92)
}

export function sticker(text: string, opts: StickerOpts & { height?: number }): THREE.CanvasTexture {
  const h = opts.height ?? 128
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(h * opts.aspect)
  canvas.height = h
  paintSticker(canvas, text, opts)
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = 8
  texture.flipY = false // glTF UV convention
  texture.userData.withLogo = (logo: HTMLImageElement, wide = false) => {
    paintSticker(canvas, text, { ...opts, wide }, logo)
    texture.needsUpdate = true
  }
  // A sponsor's own artwork fills the whole sign (cover-fitted; the form already matched its proportion).
  texture.userData.withArt = (art: HTMLImageElement) => {
    const { width: w, height: h } = canvas
    const scale = Math.max(w / art.naturalWidth, h / art.naturalHeight)
    const dw = art.naturalWidth * scale
    const dh = art.naturalHeight * scale
    const x = canvas.getContext('2d')!
    x.clearRect(0, 0, w, h)
    x.drawImage(art, (w - dw) / 2, (h - dh) / 2, dw, dh)
    texture.needsUpdate = true
  }
  return texture
}

const images = new Map<string, Promise<HTMLImageElement>>()

export function loadImage(url: string): Promise<HTMLImageElement> {
  let p = images.get(url)
  if (!p) {
    const img = new Image()
    img.decoding = 'async'
    img.src = url
    p = img.decode().then(() => img)
    images.set(url, p)
  }
  return p
}

// Slots whose sticker carries the logo next to the name.
const LOGO_SLOTS = new Set(['pod_L', 'pod_R', 'cover_L', 'cover_R', 'back'])

function decalMaterial(texture: THREE.Texture) {
  return new THREE.MeshStandardMaterial({ map: texture, transparent: true, roughness: 0.45, polygonOffset: true, polygonOffsetFactor: -2, depthWrite: false })
}

// Slot → what goes on it and its width/height ratio.
const DECALS: Record<string, (l: Livery) => { text: string; aspect: number; panel?: string; color?: string }> = {
  pod_L: (l) => ({ text: l.name.toUpperCase(), aspect: 4.1, color: contrast(l.primary) }),
  pod_R: (l) => ({ text: l.name.toUpperCase(), aspect: 4.1, color: contrast(l.primary) }),
  cover_L: (l) => ({ text: l.name.toUpperCase(), aspect: 3.3, color: contrast(l.primary) }),
  cover_R: (l) => ({ text: l.name.toUpperCase(), aspect: 3.3, color: contrast(l.primary) }),
  nose: (l) => ({ text: String(l.number), aspect: 1, color: contrast(l.primary) }),
  plate_L: (l) => ({ text: String(l.number), aspect: 1.15, panel: l.panel ?? '#F7F7F5', color: contrast(l.panel ?? '#F7F7F5') }),
  plate_R: (l) => ({ text: String(l.number), aspect: 1.15, panel: l.panel ?? '#F7F7F5', color: contrast(l.panel ?? '#F7F7F5') }),
  chest: (l) => ({ text: String(l.number), aspect: 1, panel: '#F7F7F5', color: '#141416' }),
  roof: (l) => ({ text: String(l.number), aspect: 1.25, panel: l.panel ?? '#F7F7F5', color: contrast(l.panel ?? '#F7F7F5') }),
  strip: (l) => ({ text: l.name.toUpperCase(), aspect: 9, panel: '#141416', color: '#F7F7F5' }),
  back: (l) => ({ text: l.name.toUpperCase(), aspect: 2.8, color: contrast(l.primary) }),
}

// The livery pattern (stripe, twin, split, arrow, wave) drawn over every
// Primary surface of a car from its position in the car's frame, so the
// Blender models respect the maker's pattern like the painted ones did:
// u runs 0 (rear) → 1 (front), h 0 (floor) → 1 (roof), a 0 (centre line) → 1
// (side). The positions are baked once per template as a `carPos` attribute.
const PATTERN_INDEX: Record<string, number> = { solid: 0, stripe: 1, twin: 2, split: 3, arrow: 4, wave: 5 }

type Bounds = { min: THREE.Vector3; max: THREE.Vector3 }

function bakeCarPositions(template: THREE.Group): Bounds {
  if (template.userData.carBounds) return template.userData.carBounds as Bounds
  template.updateMatrixWorld(true)
  const inverse = template.matrixWorld.clone().invert()
  const bounds: Bounds = { min: new THREE.Vector3(Infinity, Infinity, Infinity), max: new THREE.Vector3(-Infinity, -Infinity, -Infinity) }
  const v = new THREE.Vector3()
  template.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (!mesh.isMesh || (mesh.material as THREE.Material).name.replace(/\.\d+$/, '') !== 'Primary') return
    const geometry = mesh.geometry
    if (!geometry.getAttribute('carPos')) {
      const local = mesh.matrixWorld.clone().premultiply(inverse)
      const position = geometry.getAttribute('position')
      const out = new Float32Array(position.count * 3)
      for (let i = 0; i < position.count; i++) {
        v.fromBufferAttribute(position, i).applyMatrix4(local)
        out.set([v.x, v.y, v.z], i * 3)
      }
      geometry.setAttribute('carPos', new THREE.BufferAttribute(out, 3))
    }
    const carPos = geometry.getAttribute('carPos')
    for (let i = 0; i < carPos.count; i++) {
      v.fromBufferAttribute(carPos, i)
      bounds.min.min(v)
      bounds.max.max(v)
    }
  })
  template.userData.carBounds = bounds
  return bounds
}

function patternMaterial(base: THREE.MeshStandardMaterial, livery: Livery, bounds: Bounds): THREE.MeshStandardMaterial {
  const m = base.clone()
  m.color.set(livery.primary)
  const uniforms = {
    uSecondary: { value: new THREE.Color(livery.secondary) },
    uAccent: { value: new THREE.Color(livery.accent) },
    uPattern: { value: PATTERN_INDEX[livery.pattern] ?? 1 },
    uMin: { value: bounds.min.clone() },
    uSize: { value: bounds.max.clone().sub(bounds.min).max(new THREE.Vector3(0.01, 0.01, 0.01)) },
  }
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms)
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec3 carPos;\nvarying vec3 vCarPos;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvCarPos = carPos;')
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
uniform vec3 uSecondary; uniform vec3 uAccent; uniform int uPattern; uniform vec3 uMin; uniform vec3 uSize;
varying vec3 vCarPos;
vec3 liveryPattern(vec3 primary) {
  float u = clamp((vCarPos.z - uMin.z) / uSize.z, 0.0, 1.0);
  float h = clamp((vCarPos.y - uMin.y) / uSize.y, 0.0, 1.0);
  float a = clamp(abs(vCarPos.x) / (0.5 * uSize.x), 0.0, 1.0);
  if (uPattern == 1) { if (a < 0.16) return uSecondary; if (a < 0.22) return uAccent; }
  else if (uPattern == 2) { if (a > 0.12 && a < 0.32) return uSecondary; }
  else if (uPattern == 3) { float line = 0.62 - 0.24 * u; if (h < line) return uSecondary; if (h < line + 0.05) return uAccent; }
  else if (uPattern == 4) { float s = u + 0.55 * (h - 0.1) / 0.8; if (s > 0.62 && s < 1.0) return uSecondary; if (s > 0.56 && s <= 0.62) return uAccent; if (a < 0.2 && u > 0.72) return uSecondary; }
  else if (uPattern == 5) { if (h < 0.42 + 0.18 * sin(u * 6.2832 * 2.2)) return uSecondary; }
  return primary;
}`)
      .replace('#include <color_fragment>', '#include <color_fragment>\ndiffuseColor.rgb = liveryPattern(diffuseColor.rgb);')
  }
  m.customProgramCacheKey = () => 'livery-pattern'
  return m
}

// A dressed copy of a car or driver model.
export function dress(template: THREE.Group, livery: Livery, driver?: DriverPaint): THREE.Group {
  const isCar = !template.getObjectByName('Torso')
  const bounds = isCar ? bakeCarPositions(template) : null
  const model = template.clone(true)
  const colors: Record<string, string> = {
    Primary: livery.primary, Secondary: livery.secondary, Accent: livery.accent,
    Suit: livery.primary, SuitPanel: livery.secondary, Glove: livery.secondary, Boot: livery.secondary,
    Helmet: driver?.helmet_primary ?? livery.primary, HelmetSecondary: driver?.helmet_secondary ?? livery.secondary,
    Visor: driver?.visor ?? '#141A22',
  }
  const made = new Map<string, THREE.Material>()
  const lamps: THREE.MeshBasicMaterial[] = []
  model.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (!mesh.isMesh) return
    mesh.castShadow = true
    mesh.receiveShadow = true
    const base = mesh.material as THREE.MeshStandardMaterial
    const name = base.name.replace(/\.\d+$/, '')
    // Lights glow (unlit); headlights brighten at night (see the lamps list).
    if (name === 'Light' || name === 'TailLight') {
      if (!made.has(name)) {
        const lamp = new THREE.MeshBasicMaterial({ color: name === 'Light' ? 0xd8d8d0 : 0xff2a1a })
        if (name === 'Light') lamps.push(lamp)
        made.set(name, lamp)
      }
      mesh.material = made.get(name)!
      mesh.castShadow = false
      return
    }
    if (name === 'Glass') {
      if (!made.has(name)) {
        const glass = base.clone()
        glass.transparent = true
        glass.opacity = 0.62
        glass.depthWrite = false
        glass.roughness = 0.05
        glass.envMapIntensity = 1.4
        made.set(name, glass)
      }
      mesh.material = made.get(name)!
      mesh.castShadow = false
      return
    }
    if (name.startsWith('Decal_')) {
      const slot = name.slice(6)
      const spec = DECALS[slot]
      if (!spec) return
      if (!made.has(name)) {
        const texture = sticker(spec(livery).text, spec(livery))
        made.set(name, decalMaterial(texture))
        if (livery.logo && LOGO_SLOTS.has(slot)) {
          void loadImage(livery.logo).then((img) => texture.userData.withLogo(img, !!livery.logo_wide)).catch(() => undefined)
        }
      }
      mesh.material = made.get(name)!
      mesh.castShadow = false
      return
    }
    if (colors[name]) {
      if (!made.has(name)) {
        const m = name === 'Primary' && bounds ? patternMaterial(base, livery, bounds) : base.clone()
        m.color.set(colors[name])
        if (name === 'Visor') {
          m.metalness = 0.6
          m.roughness = 0.08
        }
        made.set(name, m)
      }
      mesh.material = made.get(name)!
    }
  })
  model.userData.lamps = lamps
  return model
}

// Circuit: sponsor banners, billboards and the category boards on the garages.
export type CircuitExtras = {
  lamps: THREE.MeshBasicMaterial[] // floodlight heads and the wheel's bulbs: lit at night
  lampPoints: THREE.Vector3[] // where the floodlights are (for real lights at night)
  ferris: { rotor: THREE.Object3D; gondolas: THREE.Object3D[] } | null
}

export type SignText = string | { text: string; logo?: string | null; art?: string | null }

export function dressCircuit(circuit: THREE.Group, opts: { banners: SignText[]; billboards: SignText[]; categories: string[]; bridge?: SignText | null; ferris?: SignText | null }): CircuitExtras {
  const lamp = new THREE.MeshBasicMaterial({ color: 0x8a8a80 })
  const lampPoints: THREE.Vector3[] = []
  circuit.updateMatrixWorld(true)
  // The footbridge carries the highest-numbered banner (circuit.py builds it after the gantries).
  let bridgeBanner = -1
  circuit.traverse((o) => {
    const m = ((o as THREE.Mesh).material as THREE.Material | undefined)?.name?.match(/^Banner_(\d+)/)
    if (m) bridgeBanner = Math.max(bridgeBanner, Number(m[1]))
  })
  circuit.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (!mesh.isMesh) return
    mesh.receiveShadow = true
    const material = mesh.material as THREE.MeshStandardMaterial
    // Matte ground surfaces: no glare on asphalt, grass or sand.
    if (/^(Asphalt|Grass|GrassLight|Sand|Runoff|Line|Concrete|Kerb)/.test(material.name)) {
      material.roughness = 0.95
      material.metalness = 0
      material.envMapIntensity = 0.25
    }
    const name = material.name.replace(/\.\d+$/, '')
    if (name === 'Lamp') {
      mesh.material = lamp
      if (mesh.name.startsWith('PoleHead')) lampPoints.push(mesh.getWorldPosition(new THREE.Vector3()))
      return
    }
    if (name === 'Water') {
      material.roughness = 0.04
      material.metalness = 0.25
      material.envMapIntensity = 1.2
      return
    }
    const pick = (list: SignText[], n: number) => list[n % Math.max(1, list.length)] ?? 'LAUNCHGP'
    let sign: SignText | null = null
    let aspect = 6
    let panel = '#FF5A1F'
    const m = name.match(/^(Banner|Billboard|Garage|Ferris)_(\d+)$/)
    if (!m) return
    const n = Number(m[2])
    if (m[1] === 'Banner') sign = opts.bridge !== undefined && n === bridgeBanner && n > 0 ? (opts.bridge ?? 'YOUR BRAND HERE') : pick(opts.banners, n)
    if (m[1] === 'Ferris') {
      sign = opts.ferris ?? 'YOUR BRAND HERE'
      aspect = 4.6
      panel = '#14161A'
    }
    if (m[1] === 'Billboard') {
      sign = pick(opts.billboards, n)
      aspect = 4
      panel = ['#FF5A1F', '#14161A', '#4C9AFF', '#F3F3F1'][n % 4]
    }
    if (m[1] === 'Garage') {
      sign = opts.categories[n] ?? ''
      aspect = 5
      panel = '#14161A'
    }
    if (sign === null) return
    const text = typeof sign === 'string' ? sign : sign.text
    const logo = typeof sign === 'string' ? null : sign.logo
    const art = typeof sign === 'string' ? null : sign.art
    const texture = sticker(text.toUpperCase(), { aspect, panel, color: contrast(panel), italic: true, height: art ? 256 : 128 })
    // A sponsor's own artwork replaces the painted name; otherwise its logo sits on the left of the name.
    if (art) void loadImage(art).then((img) => texture.userData.withArt(img)).catch(() => undefined)
    else if (logo) void loadImage(logo).then((img) => texture.userData.withLogo(img)).catch(() => undefined)
    mesh.material = new THREE.MeshStandardMaterial({ map: texture, roughness: 0.6 })
  })
  const rotor = circuit.getObjectByName('FerrisRotor')
  const gondolas: THREE.Object3D[] = []
  rotor?.traverse((o) => o.name.startsWith('Gondola_') && gondolas.push(o))
  return { lamps: [lamp], lampPoints, ferris: rotor ? { rotor, gondolas } : null }
}

export type GlbCar = { group: THREE.Group; wheels: THREE.Object3D[]; lamps: THREE.MeshBasicMaterial[]; dispose: () => void }

// The driver's helmet, lifted from the driver model (so the cockpit matches).
function helmetFrom(driverModel: THREE.Group): THREE.Group {
  const helmet = new THREE.Group()
  const head = driverModel.getObjectByName('Helmet')
  if (!head) return helmet
  const centre = head.getWorldPosition(new THREE.Vector3())
  for (const name of ['Helmet', 'HelmetSpoiler']) {
    const node = driverModel.getObjectByName(name)
    if (!node) continue
    const copy = node.clone(true)
    copy.position.copy(node.getWorldPosition(new THREE.Vector3()).sub(centre))
    helmet.add(copy)
  }
  return helmet
}

// A race car from the Blender model, dressed, with the driver's helmet in the cockpit.
export async function glbCar(livery: Livery, driver?: DriverPaint): Promise<GlbCar> {
  const [car, pilot] = await Promise.all([loadModel('formula'), loadModel('driver')])
  return glbCarFrom(car, pilot, livery, driver)
}

// Same, from templates already loaded (synchronous). helmet: false leaves the
// cockpit empty, for scenes where the driver stands next to the car.
export function glbCarFrom(car: THREE.Group, pilot: THREE.Group, livery: Livery, driver?: DriverPaint, { helmet: withHelmet = true } = {}): GlbCar {
  const group = dress(car, livery)
  if (!withHelmet) {
    const wheels = ['Wheel_FL', 'Wheel_FR', 'Wheel_RL', 'Wheel_RR'].map((n) => group.getObjectByName(n)).filter(Boolean) as THREE.Object3D[]
    return { group, wheels, lamps: group.userData.lamps ?? [], dispose: () => undefined }
  }
  const dressedPilot = dress(pilot, livery, driver)
  dressedPilot.updateMatrixWorld(true)
  const helmet = helmetFrom(dressedPilot)
  const seat = group.getObjectByName('Seat')
  if (seat) helmet.position.copy(seat.position) // closed cars mark the driver's seat
  else helmet.position.set(0, 0.93, 0.1)
  helmet.scale.setScalar(0.92)
  group.add(helmet)
  const wheels = ['Wheel_FL', 'Wheel_FR', 'Wheel_RL', 'Wheel_RR'].map((n) => group.getObjectByName(n)).filter(Boolean) as THREE.Object3D[]
  return { group, wheels, lamps: group.userData.lamps ?? [], dispose: () => undefined }
}

export async function glbDriver(livery: Livery, driver?: DriverPaint): Promise<THREE.Group> {
  return dress(await loadModel('driver'), livery, driver)
}

// Driver poses for the share card and the garage (joints from art/blender/driver.py).
export const POSES = ['normal', 'crossed', 'trophy', 'arms_up', 'redeemer'] as const
export type Pose = (typeof POSES)[number]

type Joint = { x?: number; y?: number; z?: number }
type PoseSpec = { shoulder: Joint; elbow: Joint; shoulderR?: Joint; elbowR?: Joint; neck?: Joint }

// Angles for the left side (x > 0); the right side mirrors z and y.
// Abduction (arm out and up) is a turn about z, raising forward a turn about -x.
const POSE_SPECS: Record<Pose, PoseSpec> = {
  normal: { shoulder: { z: 0.06 }, elbow: { x: -0.12 } },
  crossed: {
    shoulder: { x: -0.42, z: 0.12 }, elbow: { z: -1.6, x: -0.25 },
    shoulderR: { x: -0.52, z: 0.12 }, elbowR: { z: -1.58, x: -0.12 },
  },
  trophy: { shoulder: { z: 2.92, x: -0.15 }, elbow: { z: 0.3 } },
  arms_up: { shoulder: { z: 2.45, x: -0.12 }, elbow: { z: 0.1 } },
  redeemer: { shoulder: { z: 1.55 }, elbow: { z: 0 }, neck: { x: 0.5 } },
}

let trophyMesh: THREE.Group | null = null

// A gold cup, held overhead in the trophy pose.
function trophy(): THREE.Group {
  if (trophyMesh) return trophyMesh.clone()
  const gold = new THREE.MeshStandardMaterial({ color: 0xf2c14e, metalness: 1, roughness: 0.28 })
  const profile = [[0, 0], [0.09, 0], [0.09, 0.03], [0.03, 0.05], [0.025, 0.14], [0.05, 0.17], [0.11, 0.2], [0.13, 0.34], [0.12, 0.36], [0, 0.3]]
  const cup = new THREE.Mesh(new THREE.LatheGeometry(profile.map(([x, y]) => new THREE.Vector2(x, y)), 24), gold)
  const group = new THREE.Group()
  group.add(cup)
  for (const s of [1, -1]) {
    const handle = new THREE.Mesh(new THREE.TorusGeometry(0.055, 0.012, 8, 16, Math.PI), gold)
    handle.position.set(s * 0.125, 0.27, 0)
    handle.rotation.z = s > 0 ? -Math.PI / 2 : Math.PI / 2
    group.add(handle)
  }
  group.traverse((o) => ((o as THREE.Mesh).castShadow = true))
  trophyMesh = group
  return group.clone()
}

export function poseDriver(model: THREE.Object3D, pose: Pose): void {
  const spec = POSE_SPECS[pose] ?? POSE_SPECS.normal
  const apply = (name: string, joint: Joint | undefined, mirror: boolean) => {
    const node = model.getObjectByName(name)
    if (!node) return
    node.rotation.set(joint?.x ?? 0, (joint?.y ?? 0) * (mirror ? -1 : 1), (joint?.z ?? 0) * (mirror ? -1 : 1))
  }
  apply('Shoulder_L', spec.shoulder, false)
  apply('Elbow_L', spec.elbow, false)
  apply('Shoulder_R', spec.shoulderR ?? spec.shoulder, true)
  apply('Elbow_R', spec.elbowR ?? spec.elbow, true)
  apply('NeckJoint', spec.neck, false)
  model.getObjectByName('Trophy')?.removeFromParent()
  if (pose === 'trophy') {
    const cup = trophy()
    cup.name = 'Trophy'
    cup.position.set(0, 2.0, 0.08)
    model.add(cup)
  }
}
